import React, { useEffect, useMemo, useState } from 'react';
import chroma from 'chroma-js';
import { withWindow } from '@shared/utils/withWindow';
import { throttle } from 'lodash-es';
import { StyledStickyContainer, StyledContentWrapper } from './StickyNavBar.styles';
import { useStickyNavBarOffsetData } from '../StickyNavBarOffset';
import { useWeddingCodeBannerOffset } from '@apps/guest/packages/layout-engine/components/WeddingCodeBanner';
import { useSmartAppBannerProvider } from '@shared/components/SmartAppBanner';
import { pxToRem } from '@withjoy/joykit/theme';
import type { NavBarProps } from '../../layouts/LayoutBrannan/components/NavBar';
import { NavBar } from '../../layouts/LayoutBrannan/components/NavBar';
import type { NavbarEventPropsInterface } from '../../layouts/layout.types';

interface StickyNavBarProps
  extends Readonly<Omit<NavBarProps, 'variant'>>,
    Readonly<{
      eventProps: NavbarEventPropsInterface;
      pageColor?: string;
      scrollableContainerRef: React.RefObject<HTMLDivElement>;
    }> {}

export const StickyNavBar: React.FC<StickyNavBarProps> = ({ pageColor, scrollableContainerRef, ...navBarProps }) => {
  const [isScrolledDown, setIsScrolledDown] = useState(false);
  const { offset, isVisible: isVisibleFromContext } = useStickyNavBarOffsetData();
  // This offset previously only accounted for the host-banner offset, never the smart-app banner
  // or the wedding-code banner. All three render position: fixed at the top of the page, so
  // without this the sticky bar's content sat in the same space as whichever banner was actually
  // showing instead of below it. Mirrors the mobile StickyNavVariant's `top` calculation exactly
  // (Math.max(smartAppBannerHeight, offset) + weddingBannerHeight): the smart-app banner and the
  // host banner are mutually exclusive so the larger one wins, and the wedding-code banner stacks
  // on top of that since it can appear alongside either.
  const { smartAppBannerHeight } = useSmartAppBannerProvider();
  const { weddingBannerHeight } = useWeddingCodeBannerOffset();

  useEffect(() => {
    const handleScroll = throttle(() => {
      const scrollTop = scrollableContainerRef.current?.scrollTop || 0;
      withWindow(global => setIsScrolledDown(scrollTop > global.innerHeight));
    }, 100);

    const container = scrollableContainerRef.current;

    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }

    return () => undefined;
  }, [scrollableContainerRef]);

  const backgroundColor = useMemo(() => {
    return chroma(pageColor || '#ffffff')
      .alpha(0.8)
      .css();
  }, [pageColor]);

  // Combine scroll state with context visibility - navbar shows only when scrolled down AND context allows it
  const shouldShowNavBar = isScrolledDown && isVisibleFromContext;

  const topOffset = Math.max(smartAppBannerHeight, offset) + weddingBannerHeight;

  return (
    <StyledStickyContainer
      backgroundColor={backgroundColor}
      isVisible={shouldShowNavBar}
      paddingTop={pxToRem(topOffset)}
    >
      <StyledContentWrapper>
        <NavBar
          {...navBarProps}
          variant="inline"
          paddingTop={0}
          expandToFullWidth
        />
      </StyledContentWrapper>
    </StyledStickyContainer>
  );
};
